import { useState } from 'react';
import {
  deleteIdeaFromOutbox,
  deleteInsightFromOutbox,
  type OutboxIdea,
  type OutboxInsight,
} from './ecwOutbox';
import type { RetryOneResult as EcwRetryOneResult } from './useEcwOutbox';

/**
 * 気づき / 処方箋 outbox の一覧 + 手動操作 UI (POC)。
 *
 * `apps/user/src/features/posts/OutboxList.tsx` を雛形に、`insights` / `ideas` の
 * 2 種類の item を section 分けして表示する:
 *   - 種別バッジ (📝 気づき / 💡 処方箋) + title + 作成日時 + nickname
 *   - failed item に「再試行」 button (onRetryInsight / onRetryIdea あり時)
 *   - 取消 button (confirm() してから delete{Insight|Idea}FromOutbox)
 *   - pending = bg-notice-50 / border-warning-300、failed = bg-danger-50 / border-error-300
 *
 * `useEcwOutbox` の BroadcastChannel 経由で state は自動 refresh されるため、
 * 親側で props を更新する必要は無い (削除後の再描画は useEcwOutbox に任せる)。
 */

export interface EcwOutboxListProps {
  insights: OutboxInsight[];
  ideas: OutboxIdea[];
  /** 取消成功時 (id + kind)。 */
  onCancelled?: (kind: 'insight' | 'idea', id: number) => void;
  /** 個別 retry。useEcwOutbox.retryInsight を渡す想定。省略時は failed item の「再試行」 button を非表示。 */
  onRetryInsight?: (id: number) => Promise<EcwRetryOneResult>;
  /** 個別 retry。useEcwOutbox.retryIdea を渡す想定。 */
  onRetryIdea?: (id: number) => Promise<EcwRetryOneResult>;
}

type ItemKind = 'insight' | 'idea';

interface RowProps {
  kind: ItemKind;
  /** 安定 React key (id が同じでも kind が違えば衝突しないように prefix 付与)。 */
  rowKey: string;
  id: number | undefined;
  title: string;
  nickname: string;
  createdLocalAt: number;
  attempts: number;
  status: 'pending' | 'failed' | undefined;
  lastError: string | null;
  onCancel: () => Promise<void>;
  onRetry?: () => Promise<EcwRetryOneResult>;
}

function EcwOutboxRow({
  kind,
  title,
  nickname,
  createdLocalAt,
  attempts,
  status,
  lastError,
  onCancel,
  onRetry,
}: RowProps) {
  const [retrying, setRetrying] = useState(false);
  const isFailed = status === 'failed';
  const createdLocal = new Date(createdLocalAt).toLocaleString('ja-JP');
  const kindLabel = kind === 'insight' ? '📝 気づき' : '💡 処方箋';
  const ariaTitle = title || '(無題)';

  return (
    <li
      className={`mb-2 p-2 rounded-md ${
        isFailed ? 'bg-danger-50 border border-error-300' : 'bg-notice-50 border border-warning-300'
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
            isFailed ? 'bg-danger-600 text-white' : 'bg-warning-300 text-warning-800'
          }`}
        >
          {isFailed ? '🔴 送信失敗' : '📤 未送信'}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-300 text-slate-700">
          {kindLabel}
        </span>
        <strong className="text-slate-900">{ariaTitle}</strong>
        {attempts > 0 && <span className="text-[10px] text-danger-600">⚠ {attempts} 回失敗</span>}
        <span className="text-slate-400 text-xs">by {nickname || '匿名'}</span>
        <span className="text-slate-400 text-xs">作成: {createdLocal}</span>
        {isFailed && onRetry && (
          <button
            type="button"
            disabled={retrying}
            onClick={async () => {
              setRetrying(true);
              try {
                await onRetry();
              } finally {
                setRetrying(false);
              }
            }}
            aria-label={`${ariaTitle} の未送信${kind === 'insight' ? '気づき' : '処方箋'}を再送信`}
            className="text-xs text-primary-700 hover:text-primary-800 underline bg-transparent border-none p-0 cursor-pointer ml-auto disabled:opacity-50 disabled:cursor-wait"
          >
            {retrying ? '送信中...' : '再試行'}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          aria-label={`${ariaTitle} の未送信${kind === 'insight' ? '気づき' : '処方箋'}を取消`}
          className={`text-xs text-danger-600 hover:text-danger-700 underline bg-transparent border-none p-0 cursor-pointer ${
            isFailed && onRetry ? 'ml-2' : 'ml-auto'
          }`}
        >
          取消
        </button>
      </div>
      {lastError && (
        <div className="text-danger-600 text-[11px] mt-1 ml-1">最後のエラー: {lastError}</div>
      )}
    </li>
  );
}

export function EcwOutboxList({
  insights,
  ideas,
  onCancelled,
  onRetryInsight,
  onRetryIdea,
}: EcwOutboxListProps) {
  const total = insights.length + ideas.length;
  if (total === 0) return null;

  const failedCount =
    insights.filter((i) => i.status === 'failed').length +
    ideas.filter((i) => i.status === 'failed').length;
  const pendingCount = total - failedCount;

  return (
    <div className="mt-3 px-3">
      <h3 className="text-xs font-bold text-notice-700 mb-1">
        📤 未送信の気づき/処方箋 ({total})
        {failedCount > 0 && (
          <span className="ml-2 text-danger-700">
            (保留 {pendingCount} / <strong>失敗 {failedCount}</strong>)
          </span>
        )}
      </h3>
      {insights.length > 0 && (
        <section aria-label="未送信の気づき" className="mb-2">
          <h4 className="text-[11px] font-semibold text-slate-600 mb-1">
            📝 気づき ({insights.length})
          </h4>
          <ul className="pl-0 m-0 list-none">
            {insights.map((item) => (
              <EcwOutboxRow
                key={`insight-${item.id ?? '?'}`}
                kind="insight"
                rowKey={`insight-${item.id ?? '?'}`}
                id={item.id}
                title={item.title}
                nickname={item.nickname}
                createdLocalAt={item.createdLocalAt}
                attempts={item.attempts}
                status={item.status}
                lastError={item.lastError}
                onCancel={async () => {
                  if (item.id === undefined) return;
                  if (!confirm(`「${item.title || '(無題)'}」の送信を取消しますか?`)) return;
                  await deleteInsightFromOutbox(item.id);
                  onCancelled?.('insight', item.id);
                }}
                onRetry={
                  onRetryInsight && item.id !== undefined
                    ? () => onRetryInsight(item.id as number)
                    : undefined
                }
              />
            ))}
          </ul>
        </section>
      )}
      {ideas.length > 0 && (
        <section aria-label="未送信の処方箋">
          <h4 className="text-[11px] font-semibold text-slate-600 mb-1">
            💡 処方箋 ({ideas.length})
          </h4>
          <ul className="pl-0 m-0 list-none">
            {ideas.map((item) => (
              <EcwOutboxRow
                key={`idea-${item.id ?? '?'}`}
                kind="idea"
                rowKey={`idea-${item.id ?? '?'}`}
                id={item.id}
                title={item.title}
                nickname={item.nickname}
                createdLocalAt={item.createdLocalAt}
                attempts={item.attempts}
                status={item.status}
                lastError={item.lastError}
                onCancel={async () => {
                  if (item.id === undefined) return;
                  if (!confirm(`「${item.title || '(無題)'}」の送信を取消しますか?`)) return;
                  await deleteIdeaFromOutbox(item.id);
                  onCancelled?.('idea', item.id);
                }}
                onRetry={
                  onRetryIdea && item.id !== undefined
                    ? () => onRetryIdea(item.id as number)
                    : undefined
                }
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
